import {
  buildAgentFileURL,
  isAgentFileShareRoute,
  isLikelyLocalFilesystemPath,
  isVFSLikeEnvironmentId,
  requiresNativeURLResolution,
} from '@/utils/agent-file-url';
import { hasNativeJSBridge } from '@/utils/bridge-runtime';

export type PreviewSourceRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type PreviewKindOverride = 'panorama';

const hostFileExistenceCache = new Map<string, boolean>();
const previewPreparationCache = new Map<string, Promise<void>>();
const maxEagerNativePreviewBytes = 50 * 1024 * 1024;

const mimeTypeExtensions: Record<string, string> = {
  'image/avif': 'avif',
  'image/bmp': 'bmp',
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/svg+xml': 'svg',
  'image/tiff': 'tiff',
  'image/webp': 'webp',
};

async function hostFileExists(path: string): Promise<boolean> {
  const trimmedPath = path.trim();
  if (!trimmedPath) {
    return false;
  }

  const cached = hostFileExistenceCache.get(trimmedPath);
  if (cached !== undefined) {
    return cached;
  }

  try {
    const result = await window.jsb?.MessagesBridge?.checkFilesExist([
      trimmedPath,
    ]);
    const exists = Boolean(result?.[trimmedPath]);
    hostFileExistenceCache.set(trimmedPath, exists);
    return exists;
  } catch (error) {
    console.error('Failed to check file existence:', error);
    return false;
  }
}

function isHostLocalEnvironmentId(value: string | undefined): boolean {
  const normalized = value?.trim().replaceAll('_', '-').toLowerCase() ?? '';
  if (normalized === 'local-vm' || normalized.startsWith('local-vm-')) {
    return false;
  }
  return (
    normalized === '' ||
    normalized === 'local' ||
    normalized.startsWith('local-')
  );
}

export async function previewFileReference(
  path: string,
  environmentId?: string,
  sourceSessionId?: string,
  sourceRect?: PreviewSourceRect | null,
  preferredKind?: PreviewKindOverride
): Promise<void> {
  const trimmedPath = path.trim();
  if (!trimmedPath) {
    return;
  }

  try {
    if (
      isLikelyLocalFilesystemPath(trimmedPath) &&
      isHostLocalEnvironmentId(environmentId)
    ) {
      await window.jsb?.MessagesBridge?.previewHostFile(
        trimmedPath,
        sourceRect ?? null,
        preferredKind ?? null
      );
      return;
    }

    const existsOnHost = await hostFileExists(trimmedPath);
    if (!existsOnHost && environmentId) {
      if (sourceSessionId) {
        await window.jsb?.MessagesBridge?.previewWorkspaceFileForSession?.(
          sourceSessionId,
          trimmedPath,
          environmentId,
          sourceRect ?? null,
          preferredKind ?? null
        );
        return;
      }
      await window.jsb?.MessagesBridge?.previewWorkspaceFile(
        trimmedPath,
        environmentId,
        sourceRect ?? null,
        preferredKind ?? null
      );
      return;
    }

    await window.jsb?.MessagesBridge?.previewHostFile(
      trimmedPath,
      sourceRect ?? null,
      preferredKind ?? null
    );
  } catch (error) {
    console.error('Failed to preview file:', error);
  }
}

function previewAssetKey(
  source: string,
  fileName?: string,
  mimeType?: string
): string {
  return JSON.stringify([source, fileName ?? '', mimeType ?? '']);
}

function fileExtension(fileName?: string): string {
  const name = fileName?.trim() ?? '';
  const index = name.lastIndexOf('.');
  if (index < 0 || index === name.length - 1) {
    return '';
  }
  return name.slice(index + 1).toLowerCase();
}

function hasStreamableMediaHint(fileName?: string, mimeType?: string): boolean {
  const normalizedMimeType = mimeType?.trim().toLowerCase() ?? '';
  if (
    normalizedMimeType.startsWith('video/') ||
    normalizedMimeType.startsWith('audio/')
  ) {
    return true;
  }
  return ['mp4', 'm4v', 'mov', 'webm', 'mp3', 'm4a', 'wav', 'ogg'].includes(
    fileExtension(fileName)
  );
}

function isNativeAPIBackedSource(source: string): boolean {
  return (
    source.startsWith('/v1/') ||
    (source.startsWith('http') && requiresNativeURLResolution(source))
  );
}

function shouldAvoidNativePreviewMaterialization(
  source: string,
  options: {
    fileName?: string;
    mimeType?: string;
    sizeBytes?: number;
  }
): boolean {
  if (!hasNativeJSBridge()) {
    return false;
  }
  if (source.startsWith('data:') || source.startsWith('blob:')) {
    return false;
  }
  if (
    typeof options.sizeBytes === 'number' &&
    Number.isFinite(options.sizeBytes) &&
    options.sizeBytes > maxEagerNativePreviewBytes
  ) {
    return true;
  }
  if (isAgentFileShareRoute(source)) {
    return false;
  }
  if (hasStreamableMediaHint(options.fileName, options.mimeType)) {
    return true;
  }
  return isNativeAPIBackedSource(source);
}

async function openAttachmentSource(source: string): Promise<void> {
  const utilsBridge = window.jsb?.UtilsBridge;
  if (utilsBridge?.openURL) {
    await utilsBridge.openURL(source);
    return;
  }
  window.open(source, '_blank', 'noopener,noreferrer');
}

export function preparePreviewAsset(
  source: string | null | undefined,
  fileName?: string,
  mimeType?: string,
  sizeBytes?: number
): void {
  if (!hasNativeJSBridge()) {
    return;
  }

  const trimmedSource = source?.trim();
  if (!trimmedSource) {
    return;
  }
  if (
    shouldAvoidNativePreviewMaterialization(trimmedSource, {
      fileName,
      mimeType,
      sizeBytes,
    })
  ) {
    return;
  }

  const cacheKey = previewAssetKey(trimmedSource, fileName, mimeType);
  if (previewPreparationCache.has(cacheKey)) {
    return;
  }

  const task = window.jsb?.MessagesBridge?.prepareAttachmentPreview?.(
    trimmedSource,
    fileName ?? null,
    mimeType ?? null
  );
  if (!task) {
    return;
  }

  previewPreparationCache.set(
    cacheKey,
    task.catch(error => {
      console.error('Failed to prepare preview asset:', error);
      previewPreparationCache.delete(cacheKey);
    })
  );
}

export async function previewAttachmentSource(
  source: string | null | undefined,
  options: {
    fileName?: string;
    mimeType?: string;
    sizeBytes?: number;
    fallbackPath?: string;
    environmentId?: string;
    sourceSessionId?: string;
    sourceRect?: PreviewSourceRect | null;
    preferredKind?: PreviewKindOverride;
  }
): Promise<void> {
  let trimmedSource = source?.trim();

  if (
    trimmedSource &&
    hasNativeJSBridge() &&
    isAgentFileShareRoute(trimmedSource) &&
    trimmedSource.startsWith('/v1/')
  ) {
    try {
      const resolved =
        await window.jsb?.MessagesBridge?.getStorageDownloadUrl(trimmedSource);
      if (!resolved) {
        await openAttachmentSource(trimmedSource);
        return;
      }
      trimmedSource = resolved;
    } catch (error) {
      console.error('Failed to resolve signed attachment route:', error);
      await openAttachmentSource(trimmedSource);
      return;
    }
  }

  if (trimmedSource) {
    try {
      if (
        shouldAvoidNativePreviewMaterialization(trimmedSource, {
          fileName: options.fileName,
          mimeType: options.mimeType,
          sizeBytes: options.sizeBytes,
        })
      ) {
        if (options.fallbackPath && !isAgentFileShareRoute(trimmedSource)) {
          await previewFileReference(
            options.fallbackPath,
            options.environmentId,
            options.sourceSessionId,
            options.sourceRect,
            options.preferredKind
          );
          return;
        }

        await openAttachmentSource(trimmedSource);
        return;
      }

      const previewAttachment = window.jsb?.MessagesBridge?.previewAttachment;
      if (previewAttachment) {
        await previewAttachment(
          trimmedSource,
          options.fileName ?? null,
          options.mimeType ?? null,
          options.sourceRect ?? null,
          options.preferredKind ?? null
        );
        return;
      }

      window.open(trimmedSource, '_blank', 'noopener,noreferrer');
      return;
    } catch (error) {
      console.error('Failed to preview attachment source:', error);
    }
  }

  if (options.fallbackPath) {
    await previewFileReference(
      options.fallbackPath,
      options.environmentId,
      options.sourceSessionId,
      options.sourceRect,
      options.preferredKind
    );
  }
}

export async function downloadAttachmentSource(
  source: string | null | undefined,
  options: {
    fileName?: string;
    mimeType?: string;
    sizeBytes?: number;
    fallbackPath?: string;
    environmentId?: string;
    sourceSessionId?: string;
  } = {}
): Promise<void> {
  const trimmedSource = source?.trim();
  const sourceToDownload =
    trimmedSource ??
    (options.fallbackPath && isVFSLikeEnvironmentId(options.environmentId)
      ? buildAgentFileURL(options.fallbackPath)
      : undefined);

  if (!sourceToDownload) {
    return;
  }

  const fileName = suggestedDownloadFileName(
    options.fileName,
    options.mimeType,
    options.fallbackPath
  );

  if (
    hasNativeJSBridge() &&
    (options.mimeType?.startsWith('image/') ||
      sourceToDownload.startsWith('data:image/'))
  ) {
    await window.jsb?.UtilsBridge?.saveImage(sourceToDownload, fileName);
    return;
  }

  if (
    sourceToDownload.startsWith('data:') ||
    sourceToDownload.startsWith('blob:')
  ) {
    triggerBrowserDownload(sourceToDownload, fileName);
    return;
  }

  const nativeSaveAttachment = hasNativeJSBridge()
    ? window.jsb?.MessagesBridge?.saveAttachment
    : undefined;

  if (nativeSaveAttachment && isNativeAPIBackedSource(sourceToDownload)) {
    await nativeSaveAttachment(
      sourceToDownload,
      fileName,
      options.mimeType ?? null
    );
    return;
  }

  if (
    shouldAvoidNativePreviewMaterialization(sourceToDownload, {
      fileName,
      mimeType: options.mimeType,
      sizeBytes: options.sizeBytes,
    })
  ) {
    const utilsBridge = window.jsb?.UtilsBridge;
    if (utilsBridge?.openURL) {
      await utilsBridge.openURL(sourceToDownload);
      return;
    }
    triggerBrowserDownload(sourceToDownload, fileName);
    return;
  }

  try {
    const response = await fetch(sourceToDownload, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    const objectURL = window.URL.createObjectURL(blob);
    triggerBrowserDownload(
      objectURL,
      suggestedDownloadFileName(
        filenameFromContentDisposition(
          response.headers.get('content-disposition')
        ) ?? fileName,
        blob.type || options.mimeType,
        options.fallbackPath
      )
    );
    const revokeObjectURL = window.URL.revokeObjectURL?.bind(window.URL);
    if (revokeObjectURL) {
      window.setTimeout(() => {
        revokeObjectURL(objectURL);
      }, 1000);
    }
  } catch (error) {
    console.error('Failed to download attachment source:', error);
    triggerBrowserDownload(sourceToDownload, fileName);
  }
}

export function previewSourceRectForElement(
  element: Element | null | undefined
): PreviewSourceRect | null {
  if (!element) {
    return null;
  }

  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return null;
  }

  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth || 0;
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight || 0;
  const visibleLeft = Math.max(rect.left, 0);
  const visibleTop = Math.max(rect.top, 0);
  const visibleRight = Math.min(rect.right, viewportWidth);
  const visibleBottom = Math.min(rect.bottom, viewportHeight);
  const visibleWidth = visibleRight - visibleLeft;
  const visibleHeight = visibleBottom - visibleTop;

  if (visibleWidth <= 0 || visibleHeight <= 0) {
    return null;
  }

  return {
    x: visibleLeft,
    y: visibleTop,
    width: visibleWidth,
    height: visibleHeight,
  };
}

function triggerBrowserDownload(source: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = source;
  link.download = fileName;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function filenameFromContentDisposition(value: string | null): string | null {
  if (!value) {
    return null;
  }

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return utf8Match[1].trim().replace(/^"|"$/g, '');
    }
  }

  const filenameMatch = value.match(/filename=([^;]+)/i);
  return filenameMatch?.[1]?.trim().replace(/^"|"$/g, '') ?? null;
}

function suggestedDownloadFileName(
  fileName?: string,
  mimeType?: string,
  fallbackPath?: string
): string {
  const fallbackName = fallbackPath?.split('/').findLast(Boolean);
  const rawName = fileName?.trim() || fallbackName || 'bridge-image';
  const sanitizedName = rawName.replace(/[\\/:*?"<>|]+/g, '-');
  if (/\.[A-Za-z0-9]{1,8}$/.test(sanitizedName)) {
    return sanitizedName;
  }

  const extension = mimeType
    ? mimeTypeExtensions[mimeType.toLowerCase()]
    : undefined;
  return extension ? `${sanitizedName}.${extension}` : sanitizedName;
}
